import React, { useMemo, useState, useCallback } from "react";
import { AutoComplete, Card, Col, Form, Input, Row, Checkbox } from "antd";
import { connect } from "dva";
import { debounce } from "lodash";
import { LocationType } from "@/typedefs/address";
import { SecurePassAutoSend } from "@/typedefs/securePass";
import { ShipmentStatus, TrailerType } from "@/typedefs/shipment";
import { Validator } from "@/utils/validator";
import AddressBookManager from "@/components/AddressBookManager";
import useAddresses from "@/components/AddressBookManager/useAddresses";
import AddressInput from "@/components/AddressInput";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import SecurePassDropdown from "@/components/SecurePass/SecurePassDropdown/SecurePassDropdown";
import ShipmentRestrictedDaysFormItem from "@/components/ShipmentRestrictedDaysFormItem";
const { TextArea } = Input;
const cardStyle = {
    headStyle: {
        color: "white",
        backgroundColor: "#22316C",
    },
};
const LocationInformationInputs = ({ formRef, disabled: disabledProp, loading, shipmentStatus, locationType, canSaveAddress, dealerId, isAdmin, securePass, securePassInputVisible, onSelectAddress, configLang: paramConfigLang, configPerm: paramConfigPerm, }) => {
    const configPerm = useMemo(() => paramConfigPerm || {}, [paramConfigPerm]);
    const configLang = useMemo(() => paramConfigLang || {}, [paramConfigLang]);
    const [isSaveAddressDisabled, setIsSaveAddressDisabled] = useState(false);
    const [searchText, setSearchText] = useState("");
    const disabled = useMemo(() => disabledProp || (shipmentStatus !== null && shipmentStatus >= ShipmentStatus.Delivered), [disabledProp, shipmentStatus]);
    const { addresses } = useAddresses({
        dealerId,
        searchText,
        disabled,
        configPerm,
    });
    const showAddressBook = useMemo(() => {
        const hasShipmentStatus = typeof shipmentStatus === "number";
        const beforeThanCompleted = (hasShipmentStatus && shipmentStatus <= ShipmentStatus.Completed) || !hasShipmentStatus;
        return !disabled && !loading && beforeThanCompleted && configPerm["CoreService.Addresses"];
    }, [disabled, loading, shipmentStatus, configPerm]);
    const addressBookDisabled = useMemo(() => {
        if (isAdmin) {
            return dealerId === null;
        }
        return false;
    }, [isAdmin, dealerId]);
    const handleSelectAddress = useCallback((_, option) => {
        const address = addresses.find((add) => add.id === option.key);
        if (!address) {
            return;
        }
        const { id, name: locationName, addressLine1, addressLine2, city, stateId, stateName, stateShortName, zipCode, contactEmail, contactName, contactPhone, contactPhoneExtension, dayOfWeekRestricted, pickupNotes, dropOffNotes, autosendType, } = address;
        // when autosendType is not set we get 0 from BE, so we need to set the default value
        // @ts-expect-error - sometimes autosendType comes as 0 because of a BE error
        const autosendTypeValue = autosendType === 0 ? SecurePassAutoSend.Default : autosendType;
        // callback copy of the selected address
        onSelectAddress?.(address);
        formRef.current?.setFieldsValue({
            [locationType]: {
                ...formRef.current?.getFieldsValue()?.[locationType],
                originalAddressId: id,
                locationName,
                addressLine1,
                addressLine2,
                city,
                stateId,
                stateName,
                stateShortName,
                zipCode,
                contactName,
                contactPhone,
                contactPhoneExtension,
                instructionsOrRestrictions: locationType === LocationType.Pickup ? pickupNotes : dropOffNotes,
                dayOfWeekRestricted: dayOfWeekRestricted?.split(",") ?? [],
                ...(locationType === LocationType.Pickup && {
                    contactEmail,
                    autosendType: autosendTypeValue,
                }),
            },
        });
    }, [addresses, locationType, formRef, onSelectAddress]);
    return (React.createElement(Card, { className: "noLeftMarginCard", type: "inner", title: `${locationType} ${configLang.CoreService?.Information}`, headStyle: {
            color: cardStyle.headStyle.color,
            backgroundColor: cardStyle.headStyle.backgroundColor,
        } },
        showAddressBook && (React.createElement(AddressBookManager, { formRef: formRef, disabled: addressBookDisabled, locationType: locationType, onSelectAddress: onSelectAddress })),
        React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { flex: 1 },
                React.createElement(Form.Item, { name: [locationType, "locationName"], label: `${locationType} ${configLang.CoreService?.LocationName}`, rules: [{ required: true }] },
                    React.createElement(AutoComplete, { options: addresses.map(address => ({
                            value: address.name,
                            key: address.id,
                        })), onSearch: debounce(setSearchText, 500), onSelect: handleSelectAddress, disabled: disabled, placeholder: configLang.CoreService?.LocationNamePlaceholder, filterOption: (input, option) => (option?.value?.toLowerCase().indexOf(input.toLowerCase()) ?? -1) >= 0 })))),
        React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { xs: 24 },
                React.createElement(AddressInput, { disabled: disabled || (shipmentStatus !== null && shipmentStatus >= ShipmentStatus.Posted), formReference: formRef, locationType: locationType, addressLine1FormItemProps: {
                        name: [locationType, "addressLine1"],
                    }, addressLine2FormItemProps: {
                        name: [locationType, "addressLine2"],
                    }, stateIdFormItemProps: {
                        name: [locationType, "stateId"],
                    }, stateNameFormItemProps: {
                        name: [locationType, "stateName"],
                    }, stateShortNameFormItemProps: {
                        name: [locationType, "stateShortName"],
                    }, zipCodeFormItemProps: {
                        name: [locationType, "zipCode"],
                    }, cityFormItemProps: {
                        name: [locationType, "city"],
                    } }))),
        React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 12 },
                React.createElement(Form.Item, { name: [locationType, "contactName"], label: configLang.CoreService?.ContactName, rules: [{ required: true }] },
                    React.createElement(Input, { disabled: disabled }))),
            React.createElement(Col, { xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 12 },
                React.createElement(PhoneNumberInput, { disabled: disabled, locationType: locationType, formItemProps: {
                        name: "contactPhone",
                        label: configLang.CoreService?.ContactPhone,
                        formRef,
                        rules: [{ required: true }],
                    } }))),
        locationType === LocationType.Pickup && (React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { flex: 1 },
                React.createElement(Form.Item, { name: [locationType, "contactEmail"], label: "Contact Email", rules: [Validator.optional(Validator.email, "Please enter a valid email")] },
                    React.createElement(Input, { disabled: disabled }))))),
        React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { flex: 1 },
                React.createElement(Form.Item, { name: [locationType, "instructionsOrRestrictions"], label: `${locationType} ${configLang.CoreService?.InstructionsRestrictions}` },
                    React.createElement(TextArea, { disabled: disabled, rows: 4 })))),
        securePass?.enabled && securePassInputVisible && locationType === LocationType.Pickup && (React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { flex: 1 },
                React.createElement(SecurePassDropdown, { label: "SecurePass Autosend", name: [locationType, "autosendType"] })))),
        canSaveAddress && (React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { lg: 24 },
                React.createElement(Form.Item, { name: [locationType, "saveAddress"], valuePropName: "checked", shouldUpdate: (_, curValues) => {
                        if (curValues?.[locationType]?.originalAddressId != null) {
                            setIsSaveAddressDisabled(true);
                        }
                        else {
                            setIsSaveAddressDisabled(false);
                        }
                        return true;
                    } },
                    React.createElement(Checkbox, { disabled: isSaveAddressDisabled },
                        configLang.CoreService?.Save,
                        " ",
                        locationType,
                        " ",
                        configLang.CoreService?.Address))))),
        React.createElement(Row, { gutter: 16 },
            React.createElement(Col, { flex: 1 },
                React.createElement(ShipmentRestrictedDaysFormItem, { disabled: disabled, name: [locationType, "dayOfWeekRestricted"] }))),
        React.createElement(Form.Item, { name: [locationType, "addressId"], hidden: true }),
        React.createElement(Form.Item, { name: [locationType, "originalAddressId"], hidden: true })));
};
LocationInformationInputs.defaultProps = {
    canSaveAddress: false,
    dealerId: null,
    isAdmin: false,
    securePass: null,
    securePassInputVisible: true,
    loading: false,
    disabled: false,
    configPerm: {},
    configLang: {},
};
// @ts-expect-error - I added as typeof LocationInformationInputs to make typescript understand that we are exporting
// LocationInformationInputs
export default connect(({ configs }) => ({
    configLang: configs.configLang,
    securePass: configs.securePass,
    configPerm: configs.configPerm,
    // @ts-expect-error - We should not define defaultProps to use connect, but there's an eslint rule that make us do
    // it because LocationInformationInputs has optional props
}))(LocationInformationInputs);
